import ChangelogWrapper from 'components/Layout/Changelog/ChangelogWrapper';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { getRequest } from 'utils/request';
import StartMining from 'components/Layout/Mining/StartMining';
import ChangelogImage from 'components/Layout/Changelog/ChangelogImage';
import { isSimpleMiningUrl, getDomainName } from 'utils/isSimpleMiningUrl';
import { getCanonicalUrl, getMetaTranslationKey } from 'utils/getPageMetadata';
import StartComputing from 'components/Layout/Home/StartComputing';
import { navLinks } from 'components/Layout/Changelog/ChangelogNavigation/navLinks';
import { useRouter } from 'next/router';
import { DateTimeFormatOptions } from 'types/DateTimeFormatOptions';
import dynamic from 'next/dynamic';
import LoadingIndicator from 'components/LoadingIndicator';

interface Log {
  changes: any[];
  files: any[];
  id: number;
  releaseDate: string;
  formattedReleaseDate: string;
  version?: string;
  type?: string;
  ['@id']?: string;
}

interface Props {
  data: Log[];
  filterCategories?: never;
}

const dateTimeFormatOptions: DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
};

const ChangelogNavigation = dynamic(
  () => import('components/Layout/Changelog/ChangelogNavigation'),
  { ssr: false, loading: () => <LoadingIndicator height={44} /> },
);

const Changelog = (props: Props) => {
  const { data = [] } = props;
  const { t } = useTranslation();
  const { locale = 'en', asPath } = useRouter();

  const computingChangelogData = data.filter(
    ({ type }) => type === 'instance' || type === 'host',
  );

  const changelogData = isSimpleMiningUrl() ? data : computingChangelogData;

  const getMetaTitleSection = () => {
    const getNavLink = navLinks.filter(link => link?.path === asPath);

    return getNavLink.length > 0 ? t(getNavLink[0].name) : '';
  };

  return (
    <>
      <Head>
        <title>
          {t('changelog.meta.title', {
            section: getMetaTitleSection(),
            domain: getDomainName(),
          })?.trim() || 'Changelog | SimplePod.ai'}
        </title>
        <meta
          name="description"
          content={
            t(`changelog.meta.description.${getMetaTranslationKey()}`) ||
            'Latest updates and changes'
          }
        />
        <meta name="keywords" content={t('changelog.meta.keywords')} />
        <link rel="canonical" href={getCanonicalUrl(locale, 'changelog')} />
      </Head>
      <div className="changelog">
        <h1 className="section-title__header changelog__header">
          {t('changelog.header')}
        </h1>
        <ChangelogNavigation />
        {changelogData.map(log => (
          <ChangelogWrapper
            key={log.id}
            releaseDate={log.formattedReleaseDate}
            version={log.version}
          >
            <div className="changelog__entry">
              <ul>
                {log.changes
                  .sort((a, b) => a.priority - b.priority)
                  .map(change => (
                    <li className="changelog__list-item" key={change.id}>
                      <ul>
                        {change.content
                          .split('\n')
                          .map((contentElement: string, index: number) => (
                            <li
                              key={`${change.id}-${index}`}
                              className="changelog__list-item-internal-list-item"
                            >
                              {contentElement}
                              <ChangelogImage files={change.files} />
                            </li>
                          ))}
                      </ul>
                    </li>
                  ))}
              </ul>
            </div>
          </ChangelogWrapper>
        ))}
      </div>
      {isSimpleMiningUrl() ? <StartMining /> : <StartComputing />}
    </>
  );
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps(context) {
  const {
    locale = 'en',
    params: { changelog },
  } = context;
  let data = [];

  try {
    if (
      changelog &&
      (![
        'dashboard',
        'miner',
        'os',
        'rig',
        'none',
        'instance',
        'host',
      ].includes(changelog[0]) ||
        changelog.length > 1)
    ) {
      return {
        notFound: true,
      };
    }

    const requestUrl = changelog
      ? '/changelogs?type=' + changelog[0]
      : '/changelogs';
    const response = await getRequest(requestUrl);

    if (response.data) {
      data = response.data
        .sort((a, b) => b.id - a.id)
        .map(item => ({
          ...item,
          formattedReleaseDate: new Date(item.releaseDate).toLocaleDateString(
            undefined,
            dateTimeFormatOptions as Intl.DateTimeFormatOptions,
          ),
        }));
    }
  } catch (error) {
    console.error(error);
  }

  return {
    props: { data, ...(await serverSideTranslations(locale)) },
    revalidate: 60,
  };
}

export default Changelog;
